import { useState, useRef, useEffect } from 'react';
import { X, FileText, Upload } from 'lucide-react';
import { documenti as documentiApi, cantieri as cantieriApi, clienti as clientiApi } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../shared/Toast';
import { appiattisciCartelle, messaggioErroreUpload } from './cartelleUpload';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

const TIPI = [
  { value: 'preventivo',    label: 'Preventivo' },
  { value: 'contratto',     label: 'Contratto' },
  { value: 'fattura',       label: 'Fattura' },
  { value: 'foto',          label: 'Foto' },
  { value: 'ddt',           label: 'DDT' },
  { value: 'bolla',         label: 'Bolla' },
  { value: 'certificato',   label: 'Certificato' },
  { value: 'collaudo',      label: 'Collaudo' },
  { value: 'planimetria',   label: 'Planimetria' },
  { value: 'sal',           label: 'SAL' },
  { value: 'computo',       label: 'Computo metrico' },
  { value: 'corrispondenza',label: 'Corrispondenza' },
  { value: 'pdf_generico',  label: 'PDF generico' },
  { value: 'altro',         label: 'Altro' },
];

const MAX_SIZE = 50 * 1024 * 1024; // 50MB

export default function UploadDocumentoModal({ onClose, onSuccess, cartelle, hasArchivio, defaultCantiere, defaultCliente, defaultCartella, defaultFornitore, defaultTipo }) {
  var [file, setFile]         = useState(null);
  var [tipo, setTipo]         = useState(defaultTipo || 'altro');
  var [cantiere, setCantiere] = useState(defaultCantiere || '');
  var [cliente, setCliente]   = useState(defaultCliente || '');
  var [note, setNote]         = useState('');
  var [drag, setDrag]         = useState(false);
  var [uploading, setUploading] = useState(false);
  var [cantieriList, setCantieriList] = useState([]);
  var [clientiList, setClientiList]   = useState([]);
  var fileRef = useRef(null);
  var { toasts, showToast, dismissToast } = useToast();

  // Nuovi campi
  var [cartellaId, setCartellaId]       = useState(defaultCartella || '');
  var [selectedTags, setSelectedTags]   = useState([]);
  var [tagList, setTagList]             = useState([]);
  var [newTagName, setNewTagName]       = useState('');
  var [creatingTag, setCreatingTag]     = useState(false);
  var [dataDocumento, setDataDocumento] = useState('');

  useEffect(function () {
    cantieriApi.list({ page_size: 200 }).then(function (r) { setCantieriList(r.data.results || r.data); }).catch(function () {});
    clientiApi.list({ page_size: 200 }).then(function (r) { setClientiList(r.data.results || r.data); }).catch(function () {});
    if (hasArchivio) {
      documentiApi.getTag().then(function (r) { setTagList(r.data.results || r.data || []); }).catch(function () {});
    }
  }, [hasArchivio]);

  var handleFile = function (f) {
    if (f.size > MAX_SIZE) { showToast('File troppo grande (max 50MB)', 'error'); return; }
    setFile(f);
  };

  var handleDrop = function (e) {
    e.preventDefault(); setDrag(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  var handleCreateTag = async function () {
    if (!newTagName.trim() || creatingTag) return;
    setCreatingTag(true);
    try {
      var res = await documentiApi.creaTag({ nome: newTagName.trim() });
      var newTag = res.data;
      setTagList(function (prev) { return prev.concat([newTag]); });
      setSelectedTags(function (prev) { return prev.concat([String(newTag.id)]); });
      setNewTagName('');
    } catch (e) {
      showToast('Errore creazione tag', 'error');
    } finally {
      setCreatingTag(false);
    }
  };

  var handleCantiereChange = function (val) {
    setCantiere(val);
    // Auto-popola cliente se il cantiere ne ha uno e l'utente non ha gia' scelto
    if (!val) return;
    var cantiereSelezionato = cantieriList.find(function (c) { return c.id === val; });
    if (cantiereSelezionato && cantiereSelezionato.cliente && !cliente) {
      setCliente(cantiereSelezionato.cliente);
    }
  };

  var toggleTag = function (tagId) {
    setSelectedTags(function (prev) {
      var id = String(tagId);
      if (prev.indexOf(id) >= 0) return prev.filter(function (t) { return t !== id; });
      return prev.concat([id]);
    });
  };

  var handleSubmit = async function (e) {
    e.preventDefault();
    if (!file) { showToast('Seleziona un file', 'error'); return; }
    var fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipo);
    if (cantiere) fd.append('cantiere', cantiere);
    if (cliente) fd.append('cliente', cliente);
    if (defaultFornitore) fd.append('fornitore', defaultFornitore);
    if (note) fd.append('note', note);
    // DBT-156 — la cartella scelta (o quella aperta, passata come default) si
    // invia SEMPRE: il backend la rispetta. Senza cartella resta lo smistamento
    // automatico per cliente/cantiere/fornitore + tipo.
    if (cartellaId) fd.append('cartella', cartellaId);
    if (dataDocumento) fd.append('data_documento', dataDocumento);
    selectedTags.forEach(function (tagId) { fd.append('tag_ids', tagId); });
    setUploading(true);
    try {
      await documentiApi.upload(fd);
      showToast('Documento caricato', 'success');
      setTimeout(function () { onSuccess(); onClose(); }, 800);
    } catch (err) {
      showToast(messaggioErroreUpload(err), 'error');
      setUploading(false);
    }
  };

  var inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ds-text)' };
  var lbl = { display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' };

  // DBT-156 — l'API restituisce un ALBERO: si appiattisce in ordine di lettura,
  // cosi' la tendina mostra anche le sottocartelle (prima solo le radici).
  var cartellaOptions = appiattisciCartelle(cartelle).map(function (c) {
    var prefix = '';
    for (var i = 0; i < c.depth; i++) prefix += '\u00A0\u00A0\u00A0\u00A0';
    return { id: c.id, label: prefix + c.nome };
  });

  return (
    <>
      <Toast toasts={toasts} dismissToast={dismissToast} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--ds-border)' }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>Carica documento</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--ds-muted)' }}><X size={20} strokeWidth={2} /></button>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
            {/* Drop zone */}
            <div
              onClick={function () { fileRef.current.click(); }}
              onDrop={handleDrop}
              onDragOver={function (e) { e.preventDefault(); setDrag(true); }}
              onDragLeave={function () { setDrag(false); }}
              style={{ border: '2px dashed ' + (drag ? 'var(--brand)' : 'var(--ds-border)'), borderRadius: 'var(--radius-base)', padding: '24px 16px', textAlign: 'center', cursor: 'pointer', marginBottom: 16, background: drag ? tint('var(--brand)', 5) : 'transparent', transition: 'all 0.2s' }}
            >
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.xls,.zip" style={{ display: 'none' }} onChange={function (e) { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
              {file ? (
                <div>
                  <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}><FileText size={28} color="var(--ds-muted)" strokeWidth={2} /></div>
                  <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>{(file.size / 1024).toFixed(0)} KB</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}><Upload size={28} color="var(--ds-muted)" strokeWidth={2} /></div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>Trascina o clicca per caricare</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>PDF, JPG, PNG, DOCX, XLSX, ZIP \u2014 max 50MB</div>
                </div>
              )}
            </div>

            {/* Row: tipo + data documento */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Tipo *</label>
                <select required style={inp} value={tipo} onChange={function (e) { setTipo(e.target.value); }}>
                  {TIPI.map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
                </select>
              </div>
              <div>
                <label style={lbl}>Data documento</label>
                <input type="date" style={inp} value={dataDocumento} onChange={function (e) { setDataDocumento(e.target.value); }} />
              </div>
            </div>

            {/* Row: cantiere + cliente */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Cantiere (opzionale)</label>
                <select style={inp} value={cantiere} onChange={function (e) { handleCantiereChange(e.target.value); }}>
                  <option value="">{'\u2014 nessuno \u2014'}</option>
                  {cantieriList.map(function (c) { return <option key={c.id} value={c.id}>{c.titolo}</option>; })}
                </select>
              </div>
              <div>
                <label style={lbl}>Cliente (opzionale)</label>
                <select style={inp} value={cliente} onChange={function (e) { setCliente(e.target.value); }}>
                  <option value="">{'\u2014 nessuno \u2014'}</option>
                  {clientiList.map(function (c) { return <option key={c.id} value={c.id}>{c.nome}</option>; })}
                </select>
              </div>
            </div>

            {/* Row: cartella — visibile con archivio attivo, salvo il contesto fisso
                cantiere/fornitore SENZA cartella (scheda cantiere, fornitori).
                Dalla sezione Documenti arriva la cartella aperta: si mostra. */}
            {hasArchivio && cartellaOptions.length > 0 && (defaultCartella || (!defaultCantiere && !defaultFornitore)) && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Cartella (opzionale)</label>
                <select style={inp} value={cartellaId} onChange={function (e) { setCartellaId(e.target.value); }}>
                  <option value="">{'\u2014 nessuna (assegnazione automatica) \u2014'}</option>
                  {cartellaOptions.map(function (c) { return <option key={c.id} value={c.id}>{c.label}</option>; })}
                </select>
              </div>
            )}

            {/* Tag multi-select */}
            {hasArchivio && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Tag</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {tagList.map(function (t) {
                    var active = selectedTags.indexOf(String(t.id)) >= 0;
                    var tagColor = t.colore || '#6B7280';
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={function () { toggleTag(t.id); }}
                        style={{
                          padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500,
                          border: '1px solid ' + (active ? tagColor : 'var(--ds-border)'),
                          background: active ? tagColor : 'var(--ds-bg)',
                          color: active ? '#fff' : tagColor,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {t.nome}
                      </button>
                    );
                  })}
                </div>
                {/* Inline new tag */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={newTagName}
                    onChange={function (e) { setNewTagName(e.target.value); }}
                    onKeyDown={function (e) { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
                    placeholder="Nuovo tag..."
                    style={{ ...inp, flex: 1, fontSize: 11, padding: '5px 8px' }}
                  />
                  <button
                    type="button"
                    onClick={handleCreateTag}
                    disabled={creatingTag || !newTagName.trim()}
                    style={{ padding: '5px 10px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: creatingTag ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 500, opacity: creatingTag ? 0.7 : 1 }}
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Note */}
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Note (opzionale)</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 56 }} value={note} onChange={function (e) { setNote(e.target.value); }} placeholder="Descrizione o note\u2026" />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ padding: '9px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--ds-text)' }}>Annulla</button>
              <button type="submit" disabled={uploading} style={{ padding: '9px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, opacity: uploading ? 0.7 : 1 }}>
                {uploading ? 'Caricamento\u2026' : 'Carica'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
